import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSupabaseAuth } from '@kordbooks/shared-auth';
import { EyeOff, Eye } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { UntitledLogo } from '@/components/foundations/logo/untitledui-logo';
import { validateEmail, validatePasswordStrength } from '@kordbooks/shared-auth';
import { cx } from '@/utils/cx';

export default function SignUp() {
    const navigate = useNavigate();
    const { signUp, loading: authLoading, error: authError } = useSupabaseAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [agreed, setAgreed] = useState(false);

    const passwordStrength = validatePasswordStrength(password);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailError('');
        setPasswordError('');
        setConfirmPasswordError('');

        let isValid = true;

        if (!email.trim()) {
            setEmailError('Email is required');
            isValid = false;
        } else if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address');
            isValid = false;
        }

        if (!password) {
            setPasswordError('Password is required');
            isValid = false;
        } else if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            isValid = false;
        }

        if (!confirmPassword) {
            setConfirmPasswordError('Please confirm your password');
            isValid = false;
        } else if (password !== confirmPassword) {
            setConfirmPasswordError('Passwords do not match');
            isValid = false;
        }

        if (!agreed) {
            isValid = false;
        }

        if (!isValid) return;

        try {
            setIsLoading(true);
            await signUp({ email, password, confirmPassword });
            navigate('/portal');
        } catch (err) {
            const error = err as { message?: string };
            setEmailError(error.message || 'Sign up failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-dvh items-center justify-center bg-primary px-4 py-8">
            <div className="w-full max-w-sm">
                <div className="mb-8 flex justify-center">
                    <UntitledLogo className="h-8" />
                </div>

                <div className="mb-8 text-center">
                    <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
                        Create your account
                    </h1>
                </div>

                {authError && (
                    <div className="mb-6 rounded-lg bg-error-primary/10 p-4 text-sm text-error-primary">
                        {authError.message}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                        <label htmlFor="email" className="block text-sm font-medium text-primary">
                            Email address
                        </label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(value) => {
                                setEmail(value);
                                setEmailError('');
                            }}
                            isInvalid={!!emailError}
                            size="md"
                            isDisabled={isLoading || authLoading}
                        />
                        {emailError && <p className="text-sm text-error-primary">{emailError}</p>}
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="password" className="block text-sm font-medium text-primary">
                            Password
                        </label>
                        <div className="relative">
                            <Input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Create a strong password"
                                value={password}
                                onChange={(value) => {
                                    setPassword(value);
                                    setPasswordError('');
                                }}
                                isInvalid={!!passwordError}
                                size="md"
                                isDisabled={isLoading || authLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-quaternary"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? (
                                    <EyeOff className="size-5" />
                                ) : (
                                    <Eye className="size-5" />
                                )}
                            </button>
                        </div>
                        {password && (
                            <div className="text-xs text-tertiary">
                                Strength: <span className={cx(passwordStrength === 'strong' ? 'text-success' : passwordStrength === 'medium' ? 'text-warning' : 'text-error')}>
                                    {passwordStrength}
                                </span>
                            </div>
                        )}
                        {passwordError && <p className="text-sm text-error-primary">{passwordError}</p>}
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="confirm-password" className="block text-sm font-medium text-primary">
                            Confirm password
                        </label>
                        <div className="relative">
                            <Input
                                id="confirm-password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Re-enter your password"
                                value={confirmPassword}
                                onChange={(value) => {
                                    setConfirmPassword(value);
                                    setConfirmPasswordError('');
                                }}
                                isInvalid={!!confirmPasswordError}
                                size="md"
                                isDisabled={isLoading || authLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-quaternary"
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            >
                                {showConfirmPassword ? (
                                    <EyeOff className="size-5" />
                                ) : (
                                    <Eye className="size-5" />
                                )}
                            </button>
                        </div>
                        {confirmPasswordError && <p className="text-sm text-error-primary">{confirmPasswordError}</p>}
                    </div>

                    <div className="flex items-start gap-2">
                        <input
                            type="checkbox"
                            id="agree"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-1 cursor-pointer rounded border-secondary focus:ring-2 focus:ring-brand"
                            disabled={isLoading || authLoading}
                        />
                        <label htmlFor="agree" className="text-sm text-tertiary">
                            I agree to the{' '}
                            <a href="/terms" className="font-medium text-brand-secondary hover:text-brand-secondary_hover">
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a href="/privacy" className="font-medium text-brand-secondary hover:text-brand-secondary_hover">
                                Privacy Policy
                            </a>
                        </label>
                    </div>

                    <Button
                        type="submit"
                        color="primary"
                        size="md"
                        isDisabled={isLoading || authLoading || !agreed}
                        className="w-full"
                    >
                        {isLoading || authLoading ? 'Creating account...' : 'Create account'}
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm text-tertiary">
                    Already have an account?{' '}
                    <a href="/sign-in" className="font-medium text-brand-secondary hover:text-brand-secondary_hover">
                        Sign in
                    </a>
                </p>
            </div>
        </div>
    );
}
